import { useState, useEffect } from 'react';
import { webhookApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  Webhook,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';

interface WebhookLog {
  id: string;
  webhookId: string;
  url: string;
  triggeredAt: string;
  responseTime: number;
  httpStatus: number;
  success: boolean;
}

export function WebhooksPage() {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const response = await webhookApi.listWebhookLogs({
        page,
        pageSize,
      });
      if (response.code === 0) {
        setLogs(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch webhook logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, pageSize]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('zh-CN');
  };

  const getStatusBadge = (success: boolean, httpStatus: number) => {
    if (success) {
      return <Badge variant="success">{httpStatus}</Badge>;
    }
    if (httpStatus >= 400) {
      return <Badge variant="error">{httpStatus}</Badge>;
    }
    return <Badge variant="warning">{httpStatus}</Badge>;
  };

  const successRate = logs.length > 0
    ? (logs.filter(l => l.success).length / logs.length * 100).toFixed(1)
    : '0';

  const avgResponseTime = logs.length > 0
    ? (logs.reduce((sum, l) => sum + l.responseTime, 0) / logs.length).toFixed(0)
    : '0';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Webhook 记录</h1>
        <p className="text-zinc-500 mt-1">查看 Webhook 调用记录</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总调用次数</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">{total}</p>
              </div>
              <div className="p-3 bg-accent-100 rounded-xl">
                <Webhook className="w-6 h-6 text-accent-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">成功率</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">{successRate}%</p>
              </div>
              <div className="p-3 bg-success-100 rounded-xl">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">平均响应时间</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">{avgResponseTime}ms</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">失败次数</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {logs.filter(l => !l.success).length}
                </p>
              </div>
              <div className="p-3 bg-error-100 rounded-xl">
                <XCircle className="w-6 h-6 text-error-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Webhook Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>调用记录</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>状态</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>HTTP 状态</TableHead>
                    <TableHead>响应时间</TableHead>
                    <TableHead>触发时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {log.success ? (
                          <CheckCircle className="w-5 h-5 text-success-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-error-600" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-zinc-400" />
                          <span className="text-sm truncate max-w-[300px]">{log.url}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(log.success, log.httpStatus)}</TableCell>
                      <TableCell>
                        <span className={log.responseTime > 1000 ? 'text-warning-600' : 'text-zinc-900'}>
                          {log.responseTime}ms
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(log.triggeredAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
